import { useState } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/common/card"
import { cn } from "@pma/utils/helpers"
import { ChevronDown } from "lucide-react"

// ---------------------------------------------------------------------------
// ChartCard — collapsible wrapper for chart sections
// ---------------------------------------------------------------------------

export interface ChartCardProps {
  title: string
  description?: string
  children: React.ReactNode
  className?: string
  defaultOpen?: boolean
  headerRight?: React.ReactNode
}

export function ChartCard({ title, description, children, className, defaultOpen = true, headerRight }: ChartCardProps) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <Card className={cn("overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200", className)}>
      <CardHeader
        className="flex flex-row items-center justify-between space-y-0 pb-2 px-5 pt-5 cursor-pointer select-none"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0",
              !open && "-rotate-90"
            )}
          />
          <div>
            <CardTitle className="text-sm font-semibold text-foreground tracking-tight">{title}</CardTitle>
            {description && (
              <CardDescription className="mt-1 text-xs">{description}</CardDescription>
            )}
          </div>
        </div>
        {headerRight && (
          <div onClick={(e) => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
      </CardHeader>
      {open && <CardContent className="pt-0 px-5 pb-5">{children}</CardContent>}
    </Card>
  )
}
